import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen,
  ChevronRight,
  Eye,
  Grid,
  Info,
  List,
  Search,
  Sparkles,
  Star,
  ThumbsUp,
  X,
} from "@/icons";
import Button from "@/components/ui/Button.jsx";
import LayoutShell from "@/components/ui/LayoutShell.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import knowledgeBaseService from "@/services/knowledgeBaseService.js";

const BACK_CHEVRON_ICON = "/icons/appointments/e-prescription/back-chevron.svg";

function normalizeKnowledgeError(message, t) {
  const raw = String(message || "").trim();
  const lower = raw.toLowerCase();

  if (!raw) return "";
  if (lower.includes("permission denied")) {
    return t(
      "knowledgeBase.errors.permission",
      "Knowledge base content is being prepared for this account. We’re keeping the experience available while access is completed."
    );
  }
  return raw;
}

function getCategoryCount(category) {
  const value = category?.article_count;
  if (typeof value === "number") return value;
  if (Array.isArray(value) && typeof value[0]?.count === "number") return value[0].count;
  if (typeof value?.count === "number") return value.count;
  return 0;
}

export default function KnowledgeBase() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, formatDate } = useLanguage();

  const [categories, setCategories] = React.useState([]);
  const [articles, setArticles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get("q") || "");
  const [selectedCategory, setSelectedCategory] = React.useState(
    searchParams.get("category") || ""
  );
  const [viewMode, setViewMode] = React.useState(searchParams.get("view") || "grid");
  const [error, setError] = React.useState("");

  const categoryMap = React.useMemo(
    () =>
      new Map((categories || []).map((category) => [category?.category, category])),
    [categories]
  );

  const syncParams = React.useCallback(
    (next = {}) => {
      const params = new URLSearchParams();
      if (next.q) params.set("q", next.q);
      if (next.category) params.set("category", next.category);
      if (next.view && next.view !== "grid") params.set("view", next.view);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const loadInitialData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [categoriesResult, featuredResult] = await Promise.all([
        knowledgeBaseService.getCategories(),
        knowledgeBaseService.getFeaturedArticles(8),
      ]);

      if (categoriesResult?.error) {
        setError(normalizeKnowledgeError(categoriesResult.error.message, t));
      } else {
        setCategories(categoriesResult?.data || []);
      }

      if (featuredResult?.error) {
        setError((current) =>
          current || normalizeKnowledgeError(featuredResult.error.message, t)
        );
      } else {
        setArticles(featuredResult?.data || []);
      }
    } catch (nextError) {
      setError(
        normalizeKnowledgeError(
          nextError?.message ||
            t("knowledgeBase.errors.loadContent", "Failed to load knowledge base content."),
          t
        )
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  const runSearch = React.useCallback(
    async ({ query, category }) => {
      try {
        setSearchLoading(true);
        setError("");

        let result;
        if (query?.trim()) {
          result = await knowledgeBaseService.searchArticles(query.trim());
        } else if (category) {
          result = await knowledgeBaseService.getArticlesByCategory(category, 24);
        } else {
          result = await knowledgeBaseService.getFeaturedArticles(8);
        }

        if (result?.error) {
          setError(normalizeKnowledgeError(result.error.message, t));
          setArticles([]);
          return;
        }

        setArticles(result?.data || []);
        syncParams({
          q: query?.trim() || "",
          category: category || "",
          view: viewMode,
        });
      } catch (nextError) {
        setError(
          normalizeKnowledgeError(
            nextError?.message ||
              t("knowledgeBase.errors.search", "Search failed. Please try again."),
            t
          )
        );
      } finally {
        setSearchLoading(false);
      }
    },
    [syncParams, t, viewMode]
  );

  React.useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  React.useEffect(() => {
    if (!loading && selectedCategory && !searchQuery.trim()) {
      runSearch({ query: "", category: selectedCategory });
    }
  }, [loading, runSearch, searchQuery, selectedCategory]);

  React.useEffect(() => {
    syncParams({
      q: searchQuery.trim(),
      category: selectedCategory,
      view: viewMode,
    });
  }, [searchQuery, selectedCategory, syncParams, viewMode]);

  const handleSearchSubmit = async (event) => {
    event?.preventDefault();
    await runSearch({ query: searchQuery, category: selectedCategory });
  };

  const clearFilters = async () => {
    setSearchQuery("");
    setSelectedCategory("");
    await runSearch({ query: "", category: "" });
  };

  const toggleCategory = async (categoryKey) => {
    const nextCategory = selectedCategory === categoryKey ? "" : categoryKey;
    setSelectedCategory(nextCategory);
    setSearchQuery("");
    await runSearch({ query: "", category: nextCategory });
  };

  const formatArticleDate = React.useCallback(
    (value) => {
      const nextDate = value ? new Date(value) : null;
      if (!nextDate || Number.isNaN(nextDate.getTime())) {
        return t("knowledgeBase.common.recently", "Recently");
      }
      return formatDate(nextDate, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    [formatDate, t]
  );

  const resultsHeading = searchQuery.trim()
    ? t("knowledgeBase.results.search", 'Search results for "{query}"', {
        query: searchQuery.trim(),
      })
    : selectedCategory
      ? t("knowledgeBase.results.category", "{category} guides", {
          category: categoryMap.get(selectedCategory)?.display_name || "",
        })
      : t("knowledgeBase.results.featured", "Featured guides");

  const resultsSubheading = t("knowledgeBase.results.count", "{count} articles found", {
    count: articles.length,
  });

  return (
    <LayoutShell hideHeader>
      <div className="space-y-4">
        <TopBarCenter
          title={t("knowledgeBase.title", "Knowledge base")}
          onBack={() => navigate(-1)}
        />

        <SettingsCard className="overflow-hidden">
          <div className="bg-[linear-gradient(135deg,rgba(29,78,216,0.14),rgba(0,189,196,0.08),rgba(255,255,255,0.98))] px-5 py-6 md:px-6 md:py-7">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-4">
                  <div className="mt-1 flex h-12 w-12 items-center justify-center rounded-[16px] bg-[rgba(29,78,216,0.10)] text-[rgb(var(--afrimed-primary))]">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-headline-small text-on-surface">
                      {t("knowledgeBase.hero.title", "AfriMed™ Knowledge Base")}
                    </div>
                    <div className="mt-1 max-w-[720px] text-body-large text-on-surface-var">
                      {t(
                        "knowledgeBase.hero.subtitle",
                        "Find clear guides for onboarding, appointments, virtual consultations, payments, records, and everyday AfriMed™ workflows."
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 md:flex-row">
                  <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="bg-[rgba(24,50,119,0.06)] text-on-surface hover:bg-[rgba(24,50,119,0.10)]"
                  >
                    <img
                      src={BACK_CHEVRON_ICON}
                      alt=""
                      className="mr-2 h-4 w-4"
                      draggable="false"
                    />
                    {t("knowledgeBase.actions.back", "Back")}
                  </Button>
                  {(searchQuery || selectedCategory) && (
                    <Button
                      variant="tonal"
                      onClick={clearFilters}
                      iconName="X"
                      iconPosition="left"
                    >
                      {t("knowledgeBase.actions.clear", "Clear filters")}
                    </Button>
                  )}
                </div>
              </div>

              <form onSubmit={handleSearchSubmit} className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-on-surface-var" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t(
                    "knowledgeBase.search.placeholder",
                    "Search AfriMed help articles"
                  )}
                  className="h-14 w-full rounded-[16px] border border-[rgba(24,50,119,0.12)] bg-white pl-12 pr-28 text-body-large text-on-surface outline-none transition focus:border-[rgba(29,78,216,0.35)] focus:ring-2 focus:ring-[rgba(29,78,216,0.14)]"
                />
                <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(86,106,139,0.08)] text-on-surface-var transition hover:bg-[rgba(86,106,139,0.12)]"
                      aria-label={t("knowledgeBase.actions.clearSearch", "Clear search")}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                  <Button type="submit" size="sm" disabled={searchLoading}>
                    {searchLoading
                      ? t("knowledgeBase.search.searching", "Searching...")
                      : t("knowledgeBase.search.submit", "Search")}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </SettingsCard>

        <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <SettingsCard className="px-5 py-5">
              <div className="flex items-center justify-between gap-3">
                <div className="text-title-large text-on-surface">
                  {t("knowledgeBase.categories.title", "Categories")}
                </div>
                {(searchQuery || selectedCategory) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    {t("knowledgeBase.actions.reset", "Reset")}
                  </Button>
                )}
              </div>

              <div className="mt-4 space-y-2">
                {(categories || []).map((category) => {
                  const isActive = selectedCategory === category?.category;
                  return (
                    <button
                      key={category?.category}
                      type="button"
                      onClick={() => toggleCategory(category?.category)}
                      className={[
                        "flex w-full items-center gap-3 rounded-[14px] px-3 py-3 text-left transition",
                        isActive
                          ? "bg-[rgba(29,78,216,0.10)] text-[rgb(var(--afrimed-primary))]"
                          : "text-on-surface-var hover:bg-[rgba(29,78,216,0.06)] hover:text-[rgb(var(--afrimed-primary))]",
                      ].join(" ")}
                    >
                      <div
                        className={[
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px]",
                          isActive
                            ? "bg-[rgba(29,78,216,0.12)]"
                            : "bg-[rgba(86,106,139,0.08)]",
                        ].join(" ")}
                      >
                        <BookOpen className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-body-large">
                          {category?.display_name}
                        </div>
                        <div className="mt-0.5 text-body-small text-on-surface-var">
                          {t("knowledgeBase.categories.count", "{count} articles", {
                            count: getCategoryCount(category),
                          })}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </SettingsCard>

            <SettingsCard className="px-5 py-5">
              <div className="text-title-large text-on-surface">
                {t("knowledgeBase.view.title", "View")}
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "tonal"}
                  size="sm"
                  className={[
                    "flex-1 rounded-full",
                    viewMode === "grid"
                      ? "shadow-[0_6px_16px_rgba(29,78,216,0.18)]"
                      : "bg-[rgba(29,78,216,0.08)] text-[rgb(var(--afrimed-primary))] hover:bg-[rgba(29,78,216,0.12)]",
                  ].join(" ")}
                  onClick={() => setViewMode("grid")}
                >
                  <Grid className="mr-2 h-4 w-4" />
                  {t("knowledgeBase.view.grid", "Grid")}
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "tonal"}
                  size="sm"
                  className={[
                    "flex-1 rounded-full",
                    viewMode === "list"
                      ? "shadow-[0_6px_16px_rgba(29,78,216,0.18)]"
                      : "bg-[rgba(29,78,216,0.08)] text-[rgb(var(--afrimed-primary))] hover:bg-[rgba(29,78,216,0.12)]",
                  ].join(" ")}
                  onClick={() => setViewMode("list")}
                >
                  <List className="mr-2 h-4 w-4" />
                  {t("knowledgeBase.view.list", "List")}
                </Button>
              </div>

              <div className="mt-5 rounded-[16px] bg-[rgba(29,78,216,0.05)] px-4 py-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-[12px] bg-[rgba(29,78,216,0.10)] text-[rgb(var(--afrimed-primary))]">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-title-medium text-on-surface">
                      {t("knowledgeBase.tips.title", "Find answers faster")}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      {t(
                        "knowledgeBase.tips.body",
                        "Search with simple task phrases like “book a virtual consultation”, “change appointment”, or “patient records”."
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </SettingsCard>
          </aside>

          <section className="space-y-4">
            {error ? (
              <SettingsCard className="overflow-hidden">
                <div className="border-l-[4px] border-[rgb(var(--schemes-critical))] bg-[rgba(248,83,58,0.08)] px-4 py-4">
                  <div className="text-title-medium text-[rgb(var(--schemes-critical))]">
                    {t("knowledgeBase.errors.title", "Unable to load articles")}
                  </div>
                  <div className="mt-1 text-body-medium text-[rgb(var(--schemes-critical))]">
                    {error}
                  </div>
                  <div className="mt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadInitialData}
                      className="bg-[rgba(24,50,119,0.06)] text-on-surface hover:bg-[rgba(29,78,216,0.08)] hover:text-[rgb(var(--afrimed-primary))]"
                    >
                      {t("knowledgeBase.actions.tryAgain", "Try again")}
                    </Button>
                  </div>
                </div>
              </SettingsCard>
            ) : null}

            <SettingsCard className="px-5 py-5 md:px-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <div className="text-headline-small text-on-surface">{resultsHeading}</div>
                  <div className="mt-1 text-body-large text-on-surface-var">
                    {resultsSubheading}
                  </div>
                </div>
                {(searchQuery || selectedCategory) && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-[rgba(0,189,196,0.10)] px-3 py-1.5 text-label-large text-[#007C82]">
                    <Info className="h-4 w-4" />
                    <span>
                      {t(
                        "knowledgeBase.results.filteredHint",
                        "Showing articles for your current search or category."
                      )}
                    </span>
                  </div>
                )}
              </div>
            </SettingsCard>

            {loading ? (
              <SettingsCard className="px-6 py-12 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-[rgb(var(--afrimed-primary))]" />
                <div className="mt-4 text-body-large text-on-surface-var">
                  {t("knowledgeBase.loading", "Loading knowledge base...")}
                </div>
              </SettingsCard>
            ) : articles.length ? (
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 gap-4 md:grid-cols-2"
                    : "space-y-4"
                }
              >
                {articles.map((article) => {
                  const category = categoryMap.get(article?.category);
                  return (
                    <Link key={article?.id} to={`/knowledge-base/article/${article?.slug}`}>
                      <SettingsCard className="h-full px-5 py-5 transition hover:-translate-y-[1px] hover:shadow-[0_22px_54px_rgba(15,23,42,0.10)] md:px-6">
                        <div className="flex h-full flex-col">
                          <div className="flex items-start justify-between gap-3">
                            <div className="inline-flex items-center gap-2 rounded-full bg-[rgba(29,78,216,0.08)] px-3 py-1 text-label-large text-[rgb(var(--afrimed-primary))]">
                              <BookOpen className="h-3.5 w-3.5" />
                              <span>
                                {category?.display_name ||
                                  t("knowledgeBase.common.general", "General")}
                              </span>
                            </div>
                            {article?.featured ? (
                              <div className="inline-flex items-center gap-1 rounded-full bg-[rgba(240,177,0,0.12)] px-2.5 py-1 text-label-medium text-[#9A6A00]">
                                <Star className="h-3.5 w-3.5" />
                                <span>{t("knowledgeBase.common.featured", "Featured")}</span>
                              </div>
                            ) : null}
                          </div>

                          <div className="mt-4 text-title-large text-on-surface">
                            {article?.title}
                          </div>
                          <div className="mt-2 text-body-large text-on-surface-var">
                            {article?.excerpt ||
                              t(
                                "knowledgeBase.common.noExcerpt",
                                "Open this article to read the full guide."
                              )}
                          </div>

                          <div className="mt-5 flex flex-wrap items-center gap-4 text-body-small text-on-surface-var">
                            <span className="flex items-center gap-1">
                              <Eye className="h-3.5 w-3.5" />
                              <span>
                                {t("knowledgeBase.metrics.views", "{count} views", {
                                  count: article?.view_count || 0,
                                })}
                              </span>
                            </span>
                            <span className="flex items-center gap-1">
                              <ThumbsUp className="h-3.5 w-3.5" />
                              <span>
                                {t("knowledgeBase.metrics.helpful", "{count} helpful", {
                                  count: article?.helpful_votes || 0,
                                })}
                              </span>
                            </span>
                            <span>{formatArticleDate(article?.created_at)}</span>
                          </div>

                          <div className="mt-6 inline-flex items-center gap-2 text-label-large text-[rgb(var(--afrimed-primary))]">
                            <span>{t("knowledgeBase.actions.openArticle", "Open article")}</span>
                            <ChevronRight className="h-4 w-4" />
                          </div>
                        </div>
                      </SettingsCard>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <SettingsCard className="px-6 py-12 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[20px] bg-[rgba(29,78,216,0.08)] text-[rgb(var(--afrimed-primary))]">
                  <Search className="h-8 w-8" />
                </div>
                <div className="mt-5 text-headline-small text-on-surface">
                  {t("knowledgeBase.empty.title", "No articles found")}
                </div>
                <div className="mx-auto mt-2 max-w-[520px] text-body-large text-on-surface-var">
                  {searchQuery || selectedCategory
                    ? t(
                        "knowledgeBase.empty.filtered",
                        "Try a broader search term or switch to another category."
                      )
                    : t(
                        "knowledgeBase.empty.default",
                        "Knowledge base articles will appear here as soon as content is published."
                      )}
                </div>
                <div className="mt-6 flex flex-col justify-center gap-3 md:flex-row">
                  <Button onClick={() => runSearch({ query: "", category: "" })}>
                    {t("knowledgeBase.actions.showFeatured", "Show featured guides")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="bg-[rgba(24,50,119,0.06)] text-on-surface hover:bg-[rgba(24,50,119,0.10)]"
                  >
                    {t("knowledgeBase.actions.back", "Back")}
                  </Button>
                </div>
              </SettingsCard>
            )}
          </section>
        </div>
      </div>
    </LayoutShell>
  );
}
